import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { me, meKeys, setBattleEffects } from '../api/auth';

/** #14.2 / R1 (2026-09-27): battle effects are opt-in for every band and live on the server
 *  (`accounts.battle_effects`), because stains accrue there. `/me` IS the session in this app,
 *  so the preference is read from the `me` query and written through its own PATCH. False until
 *  `/me` has loaded: cosy is the default, so nothing flashes on. */
export function useGorePref(): [boolean, (on: boolean) => void] {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: meKeys.detail, queryFn: me });
  const mutation = useMutation({
    mutationFn: (on: boolean) => setBattleEffects(on ? 'splatter' : 'cosy'),
    onSuccess: (view) => qc.setQueryData(meKeys.detail, view),
  });
  const set = useCallback((on: boolean) => mutation.mutate(on), [mutation]);
  return [data?.battleEffects === 'splatter', set];
}

/** D13: gore/stains are OFF for `MINOR` and `UNKNOWN` age bands, not overridable by the toggle
 *  above — #14.2 / R1 (2026-09-27) superseded D10's "client-side only" half (the preference now
 *  lives on the server instead), but this age gate is unchanged. `=== 'ADULT'` (not `!== 'MINOR'`)
 *  folds `UNKNOWN` into the restricted side by construction, matching
 *  `NotificationService.isMinorBand`'s own C3-conservative rule — and is also correct (safe, not a
 *  bug) before `/me` has loaded, since `undefined === 'ADULT'` is `false`. */
export function ageAllowsGore(ageBand: string | null | undefined): boolean {
  return ageBand === 'ADULT';
}

/**
 * Whether permanent STAINS should be DRAWN. Single source of truth for the battle-effects
 * preference (#14.2 / R1 — it now lives on the server, but an opted-out player's existing/new
 * stains must still be hidden client-side too, not just the burst) combined with D13 (age band,
 * not overridable). Every render site — `BattleScreen`, `ProfileScreen`, `HabitatScreen`'s
 * `CompanionCard`, `WardrobeScreen` — must derive its "draw stains?" condition from this one
 * function rather than repeating `ageAllowsGore(...)` alone, or the four sites can silently drift
 * out of compliance (as they did — see RULINGS.md).
 *
 * Deliberately NOT gated on `prefers-reduced-motion`: stains are static art, not motion, so
 * reduced motion alone must never hide them (see the design spec §7). Only the shatter *burst*
 * (motion) is gated on that, separately, at its own call site.
 */
export function stainsAllowed(goreOn: boolean, ageBand: string | null | undefined): boolean {
  return goreOn && ageAllowsGore(ageBand);
}
